/* eslint-disable no-console */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useRef, useState, useContext, useMemo} from 'react';

import {TF} from '../../constants/Common';
import {SampleExcel} from '../../constants/SampleExcel';
import {ISelect} from '../../interface/ISelect';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import vehicleAPI from '../../services/modules/IP/vehicleAPI';
import vehicleFuelUsedJSON from '../../services/modules/JSON/vehicleFuelUsed';
import vehicleTypeAPI from '../../services/modules/JSON/vehicleType';
import usePlatformStore from '../../store/platformStore/platformStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
// import useUserStore from '../../store/userStore/userStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {shouldBlockReadOnly} from '../../utils/SubscriptionHelper';
import DisplayModal from '../DisplayModal/DisplayModal';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {
  facilityKeyLabel,
  yearKeyLabel
} from '../Forms/Constants/IP/commonKeyLabel';
import {VehicleKey, VehicleLabel} from '../Forms/Constants/IP/VehicleKeyLabel';
import changeUnit from '../Forms/Utils/changeUnit';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import VehicleForm from '../Forms/VehicleForm';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {
  FullInfo,
  Title,
  makeCheckKeyLabel,
  monthlyYearly,
  yesNo
} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import sumValue from './Utils/sumValue';
import TableTitleHeader from './Utils/TableTitleHeader';
import TimeFrameDataTooltip from './Utils/TimeFrameDataTooltip';

const MobileFuelTable: React.FC<{
  selectedYear: string;
  facilityId: string;
}> = ({selectedYear, facilityId}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    type: boolean;
    numberOfUnit: number;
    isLeased: boolean;
    fuelUsed: string;
    quantity: number;
    cost: number;
    driven: number;
    maintanance: number;
    comment: string;
  }
  const [facilityEfdb, setFacilityEfdb] = useState<any>({});
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [openDisplayModalData, setOpenDisplayModalData] = useState({});
  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });

  useEffect(() => {
    if (!facilityId || !selectedYear) {
      setDataSource([]);
    }
    if (facilityId && selectedYear && allJSON) {
      setIsRefresh(true);
      vehicleAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
          setFacilityEfdb(
            efdbData?.[selectedYear]?.find((i: any) => {
              return i.facilityId === facilityId;
            })
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [facilityId, selectedYear, allJSON]);

  const vehicleType = useJSONStore.getState().vehicleType;
  const vehicleFuelUsed = useJSONStore.getState().vehicleFuelUsed;
  // const {userPermission} = useUserStore.getState();

  useEffect(() => {
    // setIsRefresh(true);
    Promise.all([vehicleTypeAPI(), vehicleFuelUsedJSON()])
      .catch(() => {})
      .finally(() => {
        // setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  const [showModelInfo, setShowModelInfo] = useState(false);

  const {efdbData} = useContext(SelectedCountryContext);

  useEffect(() => {
    if (refresh && facilityId && selectedYear) {
      setIsRefresh(true);
      vehicleAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const mobileFuelKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: facilityKeyLabel.key,
      label: facilityKeyLabel.label,
      type: objTypes.facilityFormPreview,
      relatedKey: yearKeyLabel.key,
      isMaker: hasUserRole(UserRole.Maker_Role)
    },
    {
      key: VehicleKey.VEHICLE_TYPE,
      label: VehicleLabel.VEHICLE_TYPE,
      type: objTypes.json,
      format: vehicleType
    },
    {
      key: VehicleKey.NO_UNITS,
      label: VehicleLabel.NO_UNITS,
      type: objTypes.number
    },
    {
      key: VehicleKey.FUEL_USED,
      label: VehicleLabel.FUEL_USED,
      type: objTypes.json,
      format: vehicleFuelUsed
    },
    {
      key: VehicleKey.IS_LEASED,
      label: VehicleLabel.IS_LEASED,
      type: objTypes.boolean
    },
    {
      key: VehicleKey.LESSOR_NAME,
      label: VehicleLabel.LESSOR_NAME,
      type: objTypes.string,
      relatedKey: VehicleKey.IS_LEASED
    },
    {
      key: VehicleKey.MOBILE_NO,
      label: VehicleLabel.MOBILE_NO,
      type: objTypes.string,
      relatedKey: VehicleKey.IS_LEASED
    },
    {
      key: VehicleKey.RENTED_FROM,
      label: VehicleLabel.RENTED_FROM,
      type: objTypes.date,
      format: 'DD-MM-YYYY',
      relatedKey: VehicleKey.IS_LEASED
    },
    {
      key: VehicleKey.RENTED_TO,
      label: VehicleLabel.RENTED_TO,
      type: objTypes.date,
      format: 'DD-MM-YYYY',
      relatedKey: VehicleKey.IS_LEASED
    },
    {
      key: TF,
      label: [
        VehicleLabel.QUANTITY_TF,
        VehicleLabel.DRIVEN_TF,
        VehicleLabel.FUEL_COST_TF,
        VehicleLabel.MAINTENANCE_COST_TF
      ],
      type: objTypes.timeFrameData,
      renderType: 'multi'
    },
    ...(hasUserRole(UserRole.Checker_Role) ||
    hasUserRole(UserRole.Both_role) ||
    !hasUserRole(UserRole.Company_Admin)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const handleDelete = (_id: string[] | string) => {
    vehicleAPI(APITypeEnum.DELETE_ALL, {
      vehicles: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = useMemo(() => {
    return [
      {
        title: VehicleLabel.VEHICLE_TYPE,
        dataIndex: VehicleKey.VEHICLE_TYPE,
        editable: true,
        type: 'select',
        ...SearchFilter(
          VehicleLabel.VEHICLE_TYPE,
          VehicleKey.VEHICLE_TYPE,
          vehicleType
        ),
        render: (value) => {
          return (
            <>
              {
                vehicleType.find((item) => {
                  return item.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: VehicleLabel.NO_UNITS,
        dataIndex: VehicleKey.NO_UNITS,
        // width: '120px',
        editable: true,
        type: 'number',
        render: (value) => {
          return formatIndian(value);
        }
      },

      {
        title: VehicleLabel.FUEL_USED,
        dataIndex: VehicleKey.FUEL_USED,
        editable: true,
        // width: '100px',
        type: 'select',
        ...SearchFilter(
          VehicleLabel.FUEL_USED,
          VehicleKey.FUEL_USED,
          vehicleFuelUsed
        ),
        render: (value) => {
          return (
            <>
              {
                vehicleFuelUsed.find((item) => {
                  return (
                    item.value === value && item.dbName === facilityEfdb.scope1
                  );
                })?.label
              }
            </>
          );
        }
      },
      {
        title: VehicleLabel.IS_LEASED,
        dataIndex: VehicleKey.IS_LEASED,
        editable: true,
        // width: '100px',
        type: 'select',
        filterer: yesNo,
        filters: yesNo.map((item) => {
          return {text: item.label, value: item.value};
        }),
        onFilter: (value, record) => {
          return record[VehicleKey.IS_LEASED as keyof DataType] === value;
        },
        render: (value) => {
          return (
            <>
              {
                yesNo.find((item) => {
                  return item.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: VehicleLabel.QUANTITY_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        // filterer: monthlyYearly,
        // filters: monthlyYearly.map((item) => {
        //   return {text: item.label, value: item.value};
        // }),
        // onFilter: (value, record) => {
        //   return returnFilter(record, value);
        // },
        // render: (value) => {
        //   return (
        //     <TimeFrameRender value={value} dataKey={VehicleKey.QUANTITY_TF} />
        //   );
        // }
        render: (value, record: any) => {
          const mainData = value?.find((item: any) => {
            return item?.type === VehicleKey.QUANTITY_TF;
          });
          const fuelUsedValue = record[VehicleKey.FUEL_USED as keyof DataType];
          const fuelState = vehicleFuelUsed?.find((item: any) => {
            return item?.value === fuelUsedValue;
          })?.state;
          const fuelUnit = changeUnit(fuelState);

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${VehicleLabel.QUANTITY_TF} (${fuelUnit ?? ''})`,
                  dataKey: VehicleKey.QUANTITY_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(formatIndian(sumValue(mainData?.data)))} ${
                value ? fuelUnit : ''
              }`}</div>
            </div>
          );
        }
      },
      {
        title: VehicleLabel.FUEL_COST_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        filterer: monthlyYearly,
        // filters: monthlyYearly.map((item) => {
        //   return {text: item.label, value: item.value};
        // }),
        // onFilter: (value, record) => {
        //   return returnFilter(record, value);
        // },
        // render: (value) => {
        //   return (
        //     <TimeFrameRender value={value} dataKey={VehicleKey.FUEL_COST_TF} />
        //   );
        // }
        render: (value) => {
          const mainData = value?.find((item: any) => {
            return item.type === VehicleKey.FUEL_COST_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${VehicleLabel.FUEL_COST_TF} (${
                    mainData?.unit ?? ''
                  })`,
                  dataKey: VehicleKey.FUEL_COST_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${value ? mainData?.unit : ''} ${String(
                formatIndian(sumValue(mainData?.data))
              )}`}</div>
            </div>
          );
        }
      },
      {
        title: VehicleLabel.DRIVEN_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        // filterer: monthlyYearly,
        // filters: monthlyYearly.map((item) => {
        //   return {text: item.label, value: item.value};
        // }),
        // onFilter: (value, record) => {
        //   return returnFilter(record, value);
        // },
        // render: (value) => {
        //   return <TimeFrameRender value={value} dataKey={VehicleKey.DRIVEN_TF} />;
        // }
        render: (value) => {
          const mainData = value?.find((item: any) => {
            return item.type === VehicleKey.DRIVEN_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${VehicleLabel.DRIVEN_TF} (${mainData?.unit ?? ''})`,
                  dataKey: VehicleKey.DRIVEN_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(formatIndian(sumValue(mainData?.data)))} ${
                value ? mainData?.unit : ''
              }`}</div>
            </div>
          );
        }
      },
      {
        title: VehicleLabel.MAINTENANCE_COST_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        // filterer: monthlyYearly,
        // filters: monthlyYearly.map((item) => {
        //   return {text: item.label, value: item.value};
        // }),
        // onFilter: (value, record) => {
        //   return returnFilter(record, value);
        // },
        // render: (value) => {
        //   return (
        //     <TimeFrameRender
        //       value={value}
        //       dataKey={VehicleKey.MAINTENANCE_COST_TF}
        //     />
        //   );
        // }
        render: (value) => {
          const mainData = value?.find((item: any) => {
            return item.type === VehicleKey.MAINTENANCE_COST_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${VehicleLabel.MAINTENANCE_COST_TF} (${
                    mainData?.unit ?? ''
                  })`,
                  dataKey: VehicleKey.MAINTENANCE_COST_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${value ? mainData?.unit : ''} ${String(
                formatIndian(sumValue(mainData?.data))
              )}`}</div>
            </div>
          );
        }
      },
      ...(hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)
        ? [
            {
              title: makeCheckKeyLabel.STATUS_COMMENTS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <div
                    style={{
                      fontWeight: 700,
                      color: colorText.color,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}{' '}
                    {value === 'rejected' && record?.comment && (
                      <span className="customComment"> - {record.comment}</span>
                    )}
                  </div>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: makeCheckKeyLabel.STATUS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <Tag
                    onClick={() => {
                      if (shouldBlockReadOnly()) {
                        usePlatformStore
                          .getState()
                          .setOpenOnFreeTrialMessageModel(true);

                        return;
                      }
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    color={colorText.color}
                    style={{
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                  </Tag>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: FullInfo.title,
              dataIndex: 'FullInfo',
              editable: true,
              type: 'select',
              render: (_: any, record: any) => {
                return (
                  <Button
                    onClick={() => {
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    shape="circle"
                    style={{
                      marginRight: 16,
                      cursor: 'pointer',
                      color: '#435B37',
                      background: '#F5FBF3',
                      border: 0
                    }}
                  >
                    <ExclamationOutlined />
                  </Button>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: Title.actionTitle,
              dataIndex: 'operation',
              type: '' as const,
              fixed: 'right' as const,
              width: '200px',
              render: (value: any, record: any) =>
                dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                  />
                ) : null
            }
          ]
        : [])
    ];
  }, [dataSource]);

  return (
    <>
      <div>
        <TableTitleHeader
          title="Combustion Info"
          filter={{facilityId, selectedYear}}
          addButton={{
            setShowModel,
            setIsEdit,
            buttonTitle: 'Add Fuel'
          }}
          sampleDownload={{href: SampleExcel.fuelByVehicle}}
          jobs={{setShowJobs, uploadType: uploadTypes.VehicleUtility}}
          deleteButton={{selectedRowKeys, handleDelete}}
          refresh={{setRefresh}}
          shouldEdit={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
          }
        />
        <JobsModal
          open={showJobs}
          onCancel={() => {
            setShowJobs(false);
            setRefresh(true);
          }}
          type={uploadTypes.VehicleUtility}
          details={`${facilityId}/${selectedYear}`}
        />

        <TableAnt
          // getPopupContainer={(triggerNode) =>
          //   triggerNode.closest(
          //     '.ant-modal, .ant-drawer, [style*="position"]'
          //   ) as HTMLElement
          // }
          rowSelection={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
              ? {
                  type: 'checkbox',
                  selectedRowKeys,
                  getCheckboxProps: (record: any) => ({
                    disabled: record.status === statusEm.ACCEPTED
                  }),
                  onChange: (
                    selectedKey: React.Key[],
                    selectedRow: DataType[]
                  ) => {
                    setSelectedRowKeys(
                      selectedRow.map((item: any) => {
                        return item?.status !== statusEm.ACCEPTED
                          ? item._id
                          : null;
                      })
                    );
                  }
                }
              : undefined
          }
          rowKey="_id"
          dataSource={dataSource}
          columns={columns as any}
          scroll={{x: 'auto'}}
          loading={{
            indicator: <Loader />,
            spinning: allJSON ? isRefresh : true
          }}
        />
        <DrawerForm
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          setRefresh={setRefresh}
          API={vehicleAPI}
          data={isEdit ? editData : undefined}
          FormComponent={<VehicleForm />}
          idKey={`${facilityId}/${selectedYear}`}
          table={true}
          stepsTitle={[
            'Configure Details',
            'Combustion Details',
            'Quantity & Cost Details'
          ]}
          formPreview={mobileFuelKeyLabel}
        />
        <DisplayModal
          title={openDisplayModalType.title}
          open={openDisplayModal}
          onCancel={() => {
            setOpenDisplayModal(false);
          }}
          displayComponent={
            <TimeFrameDataTooltip mainData={openDisplayModalData} />
          }
        />
      </div>
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={mobileFuelKeyLabel} />
        }
        API={vehicleAPI}
        table={true}
        isChecker={true}
        isAction={false}
        stepsTitle={['Status & Comments']}
        formPreview={mobileFuelKeyLabel}
      />
    </>
  );
};

export default MobileFuelTable;
